import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { MonitorSeriesNames } from '../../../location-card.constants';
import { RowItemType, Section, UIElementType } from '../../models';
import { alarmToggleActions, disableField, enableField } from '../../actions';
import { ConfigureAlarmsFieldId } from '../common.models';
import { EchoAlarmFieldIds, EchoSectionIds } from './base-config-models';
import { field } from '../../core/field-schema';
import { required, maxLength, min, max, pattern } from '../../core/validator-builders';
import { EchoLocationCardState } from './models';
import { roundToDecimal } from '../../models/utils';
import { InstallationSeries } from 'app/shared/models/location-details';

export const getValidators = function (isMetric: boolean) {
    const validators = [];

    if (isMetric) {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
            }),
        ];
    } else {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.numeric0_100000With2DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR',
            }),
        ];
    }
};

const getFullPipeThresholdFallback = (state: EchoLocationCardState): number | undefined => {
    const details = state.locationDetails?.[0];
    const installationShape = details?.installationShapeTypeID;

    if (installationShape == null) {
        return undefined;
    }

    const isPipeShape =
        installationShape >= InstallationSeries.Pipe && installationShape < InstallationSeries.Weir;
    const isWeirShape =
        installationShape >= InstallationSeries.Weir && installationShape < InstallationSeries.Flume;

    return isPipeShape || isWeirShape ? details?.height : undefined;
};

/**
 * Echo Alarms Configuration Section
 */
export const configureAlarms = (state: EchoLocationCardState): Section => {
    const settings = state.settings;
    const config = state.monitorConfiguration;
    const fullPipeThreshold = config?.fullpipethreshold ?? getFullPipeThresholdFallback(state);

    const echoSeries = state.allMonitorSeries.find((v) => v.value === MonitorSeriesNames.echo);

    const depthAlarmValidators = [required(), ...getValidators(settings.isMetric)];

    return {
        info: {
            id: EchoSectionIds.ConfigureAlarms,
            title: 'ADD_EDIT.ADD_EDIT_CONFIGURE',
            displayTitle: true,
            expandable: true,
            expanded: false,
            preserveOldValues: false,
        },
        rows: [
            // Depth Readings to Average Row
            {
                info: {
                    id: 'unidepth-to-average-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    {
                        id: 'unidepth-to-average-label',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        label: 'ADD_EDIT.DEPTH_READINGS_TO_AVERAGE',
                        panelClass: 'alarm-slide-toggle',
                    },
                    field.number(EchoAlarmFieldIds.UnidepthToAverage, 'unidepthtoaverage', {
                        placeholder: 'ADD_EDIT.DEPTH_READINGS_TO_AVERAGE',
                        panelClass: 'alarm-input',
                        value: config?.unidepthtoaverage ?? 1,
                        inputType: 'number',
                        integerOnly: true,
                        min: 1,
                        max: 24,
                        step: 1,
                        validators: [
                            required(),
                            min({ value: 1, message: 'ADD_EDIT.DEPTH_READINGS_TO_AVERAGE_ERROR' }),
                            max({ value: 24, message: 'ADD_EDIT.DEPTH_READINGS_TO_AVERAGE_ERROR' }),
                            pattern({
                                value: REGEX_CONFIG.numeric1_24Pattern,
                            }),
                        ],
                    }),
                ],
            },

            // Low Level Alarm Row
            {
                info: {
                    id: 'low-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.LowLevelEnable, 'lowLevelEnable', {
                        placeholder: 'COMMON.LOW_LEVEL_ALARM',
                        value: config?.lowlevelenable ?? false,
                        panelClass: 'alarm-slide-toggle item-flex-center',
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.LowLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.LowLevelThreshold, 'lowLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_LOW_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(config?.lowlevelthreshold, 1),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: config && config.lowlevelenable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // Full Pipe Alarm Row
            {
                info: {
                    id: 'full-pipe-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.FullPipeEnable, 'fullPipeEnable', {
                        placeholder: 'COMMON.FULL_PIPE_ALARM',
                        panelClass: 'alarm-slide-toggle item-flex-center',
                        value: config?.fullpipeenable ?? false,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold }),
                            ],
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.FullPipeThreshold, 'fullPipethreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_FULL_PIPE',
                        placeholderSuffix: settings.depthUnit,
                        readonly: true,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(fullPipeThreshold, settings.isMetric ? 1 : 2),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: config && config.fullpipeenable ? false : true,
                        validators: getValidators(settings.isMetric),
                    }),
                ],
            },

            // High Level Alarm Row
            {
                info: {
                    id: 'high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighLevelEnable, 'highLevelEnable', {
                        placeholder: 'COMMON.HIGH_LEVEL_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value:  config?.highlevelenable ?? false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighLevelThreshold, 'highLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(config?.highlevelthreshold, 1),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: config && config.highlevelenable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // High-High Level Alarm Row
            {
                info: {
                    id: 'high-high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighHighEnable, 'highHighEnable', {
                        placeholder: 'COMMON.HIGH_HIGH_ALARAM',
                        panelClass: 'alarm-slide-toggle',
                        value: config?.highhighenable ?? false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighHighThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighHighThreshold, 'highHighThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_HIGH',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(config?.highhighthreshold, settings.isMetric ? 1 : 2),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: config && config.highhighenable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // Overflow Alarm Row - no inputs to enable/disable
            {
                info: {
                    id: 'overflow-alarm-row',
                    panelClass: 'each-configure-alarm margin-t-b-15',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.OverflowEnable, 'overflowEnable', {
                        placeholder: 'COMMON.OVERFLOW_ALARMS',
                        panelClass: 'alarm-slide-toggle',
                        value: config?.overflowenable ?? false,
                    }),
                ],
            },

            // Tilt Alarm Row - ECHO SPECIFIC
            {
                info: {
                    id: 'tilt-alarm-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(EchoAlarmFieldIds.tiltEnable, 'tiltEnable', {
                        placeholder: 'COMMON.TILT',
                        panelClass: 'alarm-slide-toggle',
                        value: config?.tiltenable ?? false,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: EchoAlarmFieldIds.tiltThreshold })
                                    : disableField({ fieldId: EchoAlarmFieldIds.tiltThreshold }),
                            ],
                        },
                    }),
                    field.number(EchoAlarmFieldIds.tiltThreshold, 'tiltThreshold', {
                        placeholder: 'COMMON.TILT_THRESHOLD_INPUT_PLACEHOLDER',
                        placeholderSuffix: 'degree',
                        panelClass: 'alarm-input',
                        value: config?.tiltthreshold,
                        disabled: config && config.tiltenable ? false : true,
                        validators: [
                            required(),
                            maxLength({ value: 100 }),
                            pattern({
                                value: settings.isMetric
                                    ? REGEX_CONFIG.installationMetricValuePattern
                                    : REGEX_CONFIG.installationValuePattern,
                                message: 'COMMON.NUMERIC_PRECISION_ERROR',
                            }),
                        ],
                    }),
                ],
            },

            // Battery Low Alarm Row
            {
                info: {
                    id: 'battery-alarm-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.BatteryLowEnable, 'batteryLowEnable', {
                        placeholder: 'COMMON.BATTERY_LOW_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: config?.batterylowenable ?? false,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.BatteryLowThreshold })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.BatteryLowThreshold }),
                            ],
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.BatteryLowThreshold, 'batteryLowThreshold', {
                        placeholder: 'COMMON.BATTERY_THRESHOLD_INPUT_PLACEHOLDER',
                        placeholderSuffix: settings.voltUnit,
                        panelClass: 'alarm-input',
                        value: config?.batterylowthreshold ?? echoSeries.batteryLowInt,
                        disabled: config && config.batterylowenable ? false : true,
                        validators: [
                            required(),
                            maxLength({ value: 100 }),
                            pattern({
                                value: settings.isMetric
                                    ? REGEX_CONFIG.installationMetricValuePattern
                                    : REGEX_CONFIG.installationValuePattern,
                                message: 'COMMON.NUMERIC_PRECISION_ERROR',
                            }),
                        ],
                    }),
                ],
            },

        ],
    };
};
